import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type ApiError } from '@/lib/api';

/** One KRA's share of a set. The UI labels a set a "group". */
export interface KraSetItem {
  id: string;
  kraGroupId: string;
  kraGroupName: string;
  weightage: number;
  goalCount: number;
}

export interface KraSet {
  id: string;
  name: string;
  description: string | null;
  departmentId: string | null;
  departmentName: string | null;
  /** Job titles this group is auto-assigned to. Many titles per group, one group per title. */
  designationIds: string[];
  designationTitles: string[];
  /** Drives canModifyKraRecord client-side — the same rule the API rejects with. */
  createdById: string | null;
  createdByName: string | null;
  totalWeightage: number;
  items: KraSetItem[];
}

export interface KraSetInput {
  name: string;
  description?: string | null;
  departmentId?: string | null;
  designationIds?: string[];
  items: Array<{ kraGroupId: string; weightage: number }>;
}

/** A save, plus who it auto-assigned to — empty when the group is not pinned. */
export interface KraSetSaveResult extends KraSet {
  autoAssigned: AssignOutcome[];
}

const KEY = ['kra-sets'] as const;

export function useKraSets(departmentId?: string, enabled = true) {
  return useQuery<KraSet[], ApiError>({
    queryKey: [...KEY, departmentId ?? 'all'],
    queryFn: () =>
      api.get<KraSet[]>(
        departmentId ? `/kra-sets?departmentId=${encodeURIComponent(departmentId)}` : '/kra-sets',
      ),
    enabled,
  });
}

function useSetMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation<TResult, ApiError, TArgs>({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KEY });
      // Saving a pinned group creates real goals, so the goal screens are stale too.
      // `goal-sets` is the overview's key prefix; `goal-set` (singular) is one
      // employee's set. Both, and neither is optional — this used to invalidate
      // `goals-overview`, which is not a key anything registers under, so the
      // assignments screen quietly kept showing pre-assign numbers.
      void queryClient.invalidateQueries({ queryKey: ['goal-set'] });
      void queryClient.invalidateQueries({ queryKey: ['goal-sets'] });
    },
  });
}

export const useCreateKraSet = () =>
  useSetMutation((body: KraSetInput) => api.post<KraSetSaveResult>('/kra-sets', body));

export const useUpdateKraSet = () =>
  useSetMutation(({ id, ...body }: KraSetInput & { id: string }) =>
    api.patch<KraSetSaveResult>(`/kra-sets/${id}`, body),
  );

export const useDeleteKraSet = () =>
  useSetMutation((id: string) => api.delete<void>(`/kra-sets/${id}`));

export type AssignSkipReason =
  | 'NOT_YOUR_REPORT'
  | 'ALREADY_ACCEPTED'
  | 'HAS_GOALS'
  | 'EXITED'
  | 'NOT_FOUND'
  | 'GROUP_INVALID';

export interface AssignOutcome {
  employeeId: string;
  employeeName: string;
  assigned: boolean;
  reason: AssignSkipReason | null;
  message: string | null;
}

/**
 * `useAssignableEmployees` used to live here, reading
 * `GET /kra-sets/assignable-employees`. It returned the same people as the goal
 * overview with the same columns, so the assign screen and the goal screen were two
 * lists of one thing — and they disagreed, because the overview was org-wide and this
 * one was scoped to the reporting line. `useGoalOverview` carries `canAssign` now.
 */
/**
 * Take the assigned goals back off people, when it was the wrong group.
 *
 * The reason is required and lands in the audit trail beside the actor and the employee.
 * This is the only action in the product that removes somebody's work, so "who did this
 * and what did they say the reason was" has to be answerable months later.
 */
export function useUnassignKraSet() {
  const queryClient = useQueryClient();
  return useMutation<AssignOutcome[], ApiError, { employeeIds: string[]; reason: string }>({
    mutationFn: (body) => api.post<AssignOutcome[]>("/kra-sets/unassign", body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["goal-set"] });
      void queryClient.invalidateQueries({ queryKey: ["goal-sets"] });
    },
  });
}

export function useAssignKraSet() {
  const queryClient = useQueryClient();
  return useMutation<AssignOutcome[], ApiError, { kraSetId: string; employeeIds: string[] }>({
    mutationFn: ({ kraSetId, employeeIds }) =>
      api.post<AssignOutcome[]>(`/kra-sets/${kraSetId}/assign`, { employeeIds }),
    onSuccess: () => {
      // The assigned goals show up on the overview and the per-employee set alike.
      void queryClient.invalidateQueries({ queryKey: ['goal-set'] });
      void queryClient.invalidateQueries({ queryKey: ['goal-sets'] });
    },
  });
}
